import { EditOutlined, ExportOutlined } from '@ant-design/icons'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import SBNiceDate from '@components/utils/SBNiceDate'
import { LegalDocument as LegalDocumentModel } from '@models/LegalDocument'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getLegalDocuments,
  selectLegalDocuments,
  setLegalDocumentQuery,
} from '@store/LegalDocumentReducer'
import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { Button, Col, Input, Space, Tooltip, Typography } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

const { Text } = Typography

const LegalDocumentsPage = () => {
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const legalDocuments = useAppSelector(selectLegalDocuments())
  const { query, isLoading } = useAppSelector((state) => state.legalDocument)

  const columns: TableColumnsType<LegalDocumentModel> = [
    {
      title: 'Friendly name',
      width: 300,
      key: 'friendlyName',
      render: (record: LegalDocumentModel) => (
        <Text>{record.friendlyName}</Text>
      ),
    },
    {
      title: 'Version',
      render: (record: LegalDocumentModel) => (
        <Text>{record.activeVersion?.version ?? '--'}</Text>
      ),
    },
    {
      title: 'Last update',
      render: (record: LegalDocumentModel) => (
        <SBNiceDate date={record.activeVersion?.updatedAt} />
      ),
    },
    {
      title: 'Action',
      align: 'right',
      render: (record: LegalDocumentModel) => (
        <Space>
          {record.frontendUrl && (
            <Tooltip title="View legal document">
              <Button
                type="primary"
                icon={<ExportOutlined />}
                href={record.frontendUrl}
                target="_blank"
              >
                View
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Edit legal document">
            <Link
              to={
                record.activeVersion
                  ? `../${record.slug}/${record.activeVersion.version}`
                  : `../${record.slug}`
              }
            >
              <Button icon={<EditOutlined />} />
            </Link>
          </Tooltip>

          <SBRM.Actions
            actions={[SBRMAction.Delete]}
            entity={Entity.legalDocument}
            entityId={record.id}
          />
        </Space>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter:
      | SorterResult<LegalDocumentModel>
      | SorterResult<LegalDocumentModel>[]
  ) => {
    const newQuery: TableParams = {
      ...query,
      pagination,
      ...extractSortDataFromSorterResult(sorter),
    }
    dispatch(setLegalDocumentQuery(newQuery))
    dispatch(getLegalDocuments(newQuery))
  }

  const fetchLegalDocuments = () => {
    const newQuery: TableParams = {
      ...query,
      columnKey: 'friendlyName',
      order: 'ascend',
    }
    dispatch(setLegalDocumentQuery(newQuery))
    dispatch(getLegalDocuments(newQuery))
  }

  useEffect(() => {
    if (isOpen) return
    fetchLegalDocuments()
  }, [isOpen])

  useEffect(() => {
    fetchLegalDocuments()
  }, [])

  return (
    <Page
      permissions={Permission.listLegalDocument}
      noWrapWithCol
      pageTitle={{
        title: 'Legal documents',
        level: 4,
        toolbar: (
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.legalDocument}
          />
        ),
      }}
    >
      <Col span={24}>
        <TableFilters
          onValuesChange={(filters, search) => {
            const newQuery: TableParams = {
              ...query,
              pagination: { ...query.pagination, current: 1 },
              columnKey: 'friendlyName',
              order: 'ascend',
              filters: filters,
              search: search,
            }
            dispatch(setLegalDocumentQuery(newQuery))
            dispatch(getLegalDocuments(newQuery))
          }}
          left={[]}
          right={[
            {
              name: 'name',
              type: 'search',
              input: <Input placeholder="Search by friendly name" allowClear />,
            },
          ]}
        />
        <SBTable
          columns={columns}
          dataSource={legalDocuments}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
        />
      </Col>
    </Page>
  )
}

export default LegalDocumentsPage
